
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { useAuth } from '@/context/AuthContext';

type ApprovalStatus = 'pending' | 'approved' | 'rejected';

type ApprovalRequest = {
  id: string;
  caseNumber: string;
  childName: string;
  type: string;
  description: string;
  amount?: number | null;
  incident_type?: string;
  priority_level?: string;
  prior_incidents?: string;
  submittedBy?: string;
  submittedAt?: string;
  status: ApprovalStatus;
  approved_remark?: string;
  rejected_remark?: string;
};

const API_BASE = 'https://guardiancloudcare.com/backend/api';

const typeLabels: Record<string, string> = {
  intervention_plan: 'Intervention Plan',
  financial_assistance: 'Financial Assistance',
  case_closure: 'Case Closure',
  escalation: 'Escalation Request'
};

const getRisk = (request: ApprovalRequest) => {
  let score = 70;

  switch (request.incident_type) {
    case 'Sexual Abuse':
    case 'Child Trafficking':
    case 'Online Sexual Exploitation':
      score = 95;
      break;
    case 'Physical Abuse':
      score = 90;
      break;
    case 'Emotional Abuse':
      score = 80;
      break;
    case 'Neglect':
    case 'Child Labor':
    case 'Abandonment':
      score = 75;
      break;
  }

  if (request.prior_incidents === 'yes') score += 5;
  if (request.priority_level === 'urgent') score += 3;
  if (request.priority_level === 'emergency') score += 5;

  score = Math.min(score, 100);

  return {
    score,
    level:
      score >= 90 ? 'Critical' :
      score >= 80 ? 'High' :
      score >= 75 ? 'Moderate' : 'Low'
  };
};

export default function SupApprovalsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [requests, setRequests] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const [selectedCase, setSelectedCase] =
    useState<ApprovalRequest | null>(null);

  const [decisionCase, setDecisionCase] =
    useState<ApprovalRequest | null>(null);

  const [decision, setDecision] =
    useState<'approved' | 'rejected'>('approved');

  const [remark, setRemark] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');

  const fetchApprovals = async () => {
    try {
      setError('');

      if (!user?.uid || user.role !== 'supervisor') {
        throw new Error('Supervisor account not found.');
      }

      const response = await fetch(
        `${API_BASE}/get_approvals.php?supervisor_uid=${encodeURIComponent(user.uid)}`
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Unable to retrieve approvals.');
      }

      setRequests(Array.isArray(result.data) ? result.data : []);

    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load approval requests.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.uid && user.role === 'supervisor') {
      fetchApprovals();
    } else {
      setLoading(false);
    }
  }, [user?.uid, user?.role]);

  const counts = useMemo(() => ({
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length
  }), [requests]);

  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      const matchesFilter =
        filter === 'all' || request.status === filter;

      const searchText = search.toLowerCase();

      const matchesSearch =
        (request.caseNumber || '').toLowerCase().includes(searchText) ||
        (request.childName || '').toLowerCase().includes(searchText) ||
        (request.submittedBy || '').toLowerCase().includes(searchText) ||
        (typeLabels[request.type] || request.type || '')
          .toLowerCase()
          .includes(searchText);

      return matchesFilter && matchesSearch;
    });
  }, [requests, filter, search]);

  const openDecision = (
    request: ApprovalRequest,
    action: 'approved' | 'rejected'
  ) => {
    setDecisionCase(request);
    setDecision(action);
    setRemark('');
  };

  const confirmDecision = async () => {
    if (!decisionCase) return;

    if (decision === 'rejected' && remark.trim() === '') {
      setError('A rejection reason is required.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const response = await fetch(`${API_BASE}/update_approval.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: decisionCase.id,
          status: decision,
          remark: remark.trim(),
          supervisor_uid: user?.uid
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.error || 'Unable to process this approval.'
        );
      }

      setToast(
        decision === 'approved'
          ? 'Case approved successfully.'
          : 'Case rejected successfully.'
      );

      setDecisionCase(null);
      setRemark('');

      await fetchApprovals();

    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const statusStyle = (status: string) => {
    if (status === 'approved') {
      return 'bg-green-50 text-green-700 border-green-200';
    }

    if (status === 'rejected') {
      return 'bg-red-50 text-red-700 border-red-200';
    }

    return 'bg-amber-50 text-amber-700 border-amber-200';
  };

  const riskStyle = (level: string) => {
    switch (level) {
      case 'Critical':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'High':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'Moderate':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      default:
        return 'bg-green-50 text-green-700 border-green-200';
    }
  };

  return (
    <AppLayout
      title="Case Approvals"
      breadcrumb={['Supervisor', 'Case Approvals']}
    >
      <div className="space-y-6 pb-8">

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Case Approval Management
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Review Social Worker submissions and make approval decisions.
            </p>
          </div>

          <button
            onClick={() => {
              setLoading(true);
              fetchApprovals();
            }}
            className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50"
          >
            <i className="ri-refresh-line" />
            Refresh
          </button>
        </div>

        {error && (
          <div className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <span>{error}</span>
            <button onClick={() => setError('')}>
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        )}

        {toast && (
          <div className="flex items-center justify-between bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
            <span className="flex items-center gap-2">
              <i className="ri-checkbox-circle-fill" />
              {toast}
            </span>
            <button onClick={() => setToast('')}>
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[
            {
              label: 'Total Submissions',
              value: counts.all,
              icon: 'ri-file-list-3-line',
              color: 'text-teal-600',
              background: 'bg-teal-50'
            },
            {
              label: 'Pending Review',
              value: counts.pending,
              icon: 'ri-time-line',
              color: 'text-amber-600',
              background: 'bg-amber-50'
            },
            {
              label: 'Approved',
              value: counts.approved,
              icon: 'ri-checkbox-circle-line',
              color: 'text-green-600',
              background: 'bg-green-50'
            },
            {
              label: 'Rejected',
              value: counts.rejected,
              icon: 'ri-close-circle-line',
              color: 'text-red-600',
              background: 'bg-red-50'
            }
          ].map(item => (
            <div
              key={item.label}
              className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{item.label}</p>
                  <h2 className="text-3xl font-bold text-gray-900 mt-2">
                    {item.value}
                  </h2>
                </div>
                <div className={`${item.background} ${item.color} w-12 h-12 rounded-xl flex items-center justify-center`}>
                  <i className={`${item.icon} text-2xl`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">

          <div className="p-5 border-b border-gray-100">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h2 className="font-bold text-gray-900">
                  Submitted Cases
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Review and manage case approval requests.
                </p>
              </div>

              <div className="relative w-full lg:w-80">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search case number or child..."
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-teal-500"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-5">
              {[
                { value: 'all', label: 'All Submissions', count: counts.all },
                { value: 'pending', label: 'Pending', count: counts.pending },
                { value: 'approved', label: 'Approved', count: counts.approved },
                { value: 'rejected', label: 'Rejected', count: counts.rejected }
              ].map(tab => (
                <button
                  key={tab.value}
                  onClick={() => setFilter(tab.value)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                    filter === tab.value
                      ? 'bg-teal-500 text-white'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                  <span className="ml-2 text-xs opacity-80">
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="py-20 text-center">
              <i className="ri-loader-4-line animate-spin text-3xl text-teal-500" />
              <p className="text-sm text-gray-500 mt-3">
                Loading submissions...
              </p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="py-20 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto">
                <i className="ri-inbox-line text-3xl text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-800 mt-4">
                No submissions found
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                There are no cases matching your current search or filter.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredRequests.map(request => {
                const risk = getRisk(request);

                return (
                  <div
                    key={request.id}
                    className="p-5 hover:bg-gray-50/70 transition"
                  >
                    <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">

                      <div className="flex items-start gap-4 min-w-0">
                        <div className="w-12 h-12 shrink-0 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center">
                          <i className="ri-user-heart-line text-2xl" />
                        </div>

                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="font-bold text-gray-900">
                              {request.childName || 'Child information unavailable'}
                            </h3>

                            <span className={`text-xs border px-2 py-1 rounded-full font-medium ${statusStyle(request.status)}`}>
                              {request.status === 'pending'
                                ? 'For Approval'
                                : request.status}
                            </span>
                          </div>

                          <p className="text-xs text-gray-500 mt-1">
                            {request.caseNumber}
                          </p>

                          <div className="flex flex-wrap items-center gap-2 mt-3">
                            <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-lg">
                              <i className="ri-file-text-line mr-1" />
                              {typeLabels[request.type] || request.type}
                            </span>

                            <span className={`text-xs border px-2.5 py-1 rounded-lg font-medium ${riskStyle(risk.level)}`}>
                              {risk.level} Risk · {risk.score}
                            </span>

                            {request.amount && (
                              <span className="text-xs bg-teal-50 text-teal-700 px-2.5 py-1 rounded-lg">
                                ₱{Number(request.amount).toLocaleString()}
                              </span>
                            )}
                          </div>

                          <p className="text-xs text-gray-500 mt-3">
                            <i className="ri-user-line mr-1" />
                            Submitted by {request.submittedBy || 'Social Worker'}
                            <span className="mx-2">•</span>
                            <i className="ri-calendar-line mr-1" />
                            {request.submittedAt || 'Date unavailable'}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                        <button
                          onClick={() => setSelectedCase(request)}
                          className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-100"
                        >
                          <i className="ri-eye-line mr-1" />
                          View Details
                        </button>

                        {request.status === 'pending' && (
                          <>
                            <button
                              onClick={() => openDecision(request, 'approved')}
                              className="px-4 py-2.5 bg-teal-500 text-white rounded-xl text-sm hover:bg-teal-600"
                            >
                              <i className="ri-check-line mr-1" />
                              Approve
                            </button>

                            <button
                              onClick={() => openDecision(request, 'rejected')}
                              className="px-4 py-2.5 bg-red-50 text-red-600 border border-red-100 rounded-xl text-sm hover:bg-red-100"
                            >
                              <i className="ri-close-line mr-1" />
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {(request.approved_remark || request.rejected_remark) && (
                      <div className="mt-4 ml-0 xl:ml-16 bg-gray-50 border border-gray-100 rounded-xl p-3">
                        <p className="text-xs font-semibold text-gray-600">
                          Supervisor Remarks
                        </p>
                        <p className="text-sm text-gray-700 mt-1">
                          {request.approved_remark || request.rejected_remark}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="px-5 py-4 border-t border-gray-100 text-xs text-gray-500">
            Showing {filteredRequests.length} of {requests.length} submissions
          </div>
        </div>
      </div>

      {selectedCase && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">

            <div className="p-5 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="font-bold text-lg text-gray-900">
                  Case Submission Details
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedCase.caseNumber}
                </p>
              </div>
              <button
                onClick={() => setSelectedCase(null)}
                className="w-9 h-9 rounded-lg hover:bg-gray-100 text-gray-500"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <p className="text-xs text-gray-500">Child Name</p>
                <h3 className="text-lg font-bold text-gray-900 mt-1">
                  {selectedCase.childName || 'Not available'}
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500">Request Type</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1">
                    {typeLabels[selectedCase.type] || selectedCase.type}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500">Submission Status</p>
                  <span className={`inline-block mt-1 text-xs border px-2.5 py-1 rounded-full ${statusStyle(selectedCase.status)}`}>
                    {selectedCase.status === 'pending'
                      ? 'For Approval'
                      : selectedCase.status}
                  </span>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500">Incident Type</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1">
                    {selectedCase.incident_type || 'Not available'}
                  </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500">Priority Level</p>
                  <p className="text-sm font-semibold text-gray-800 mt-1 capitalize">
                    {selectedCase.priority_level || 'Not available'}
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-bold text-gray-800 mb-2">
                  Social Worker Submission Description
                </h3>
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedCase.description || 'No description provided.'}
                </div>
              </div>

              {selectedCase.amount && (
                <div>
                  <p className="text-xs text-gray-500">Amount Requested</p>
                  <p className="text-xl font-bold text-teal-600 mt-1">
                    ₱{Number(selectedCase.amount).toLocaleString()}
                  </p>
                </div>
              )}

              <div className="bg-teal-50 border border-teal-100 rounded-xl p-4">
                <p className="text-sm font-semibold text-teal-800">
                  Submission Information
                </p>
                <p className="text-xs text-teal-700 mt-2">
                  Submitted by: {selectedCase.submittedBy || 'Not available'}
                </p>
                <p className="text-xs text-teal-700 mt-1">
                  Submitted at: {selectedCase.submittedAt || 'Not available'}
                </p>
              </div>

              {selectedCase.status === 'pending' && (
                <div className="flex flex-wrap justify-end gap-2 pt-2">
                  <button
                    onClick={() => {
                      setSelectedCase(null);
                      navigate(`/child-profiles/${selectedCase.caseNumber}`);
                    }}
                    className="px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50"
                  >
                    View Child Profile
                  </button>

                  <button
                    onClick={() => {
                      setSelectedCase(null);
                      openDecision(selectedCase, 'rejected');
                    }}
                    className="px-4 py-2.5 bg-red-50 text-red-600 rounded-xl text-sm"
                  >
                    Reject
                  </button>

                  <button
                    onClick={() => {
                      setSelectedCase(null);
                      openDecision(selectedCase, 'approved');
                    }}
                    className="px-4 py-2.5 bg-teal-500 text-white rounded-xl text-sm"
                  >
                    Approve Case
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {decisionCase && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden">

            <div className={`p-5 ${
              decision === 'approved' ? 'bg-teal-50' : 'bg-red-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                  decision === 'approved'
                    ? 'bg-teal-100 text-teal-600'
                    : 'bg-red-100 text-red-600'
                }`}>
                  <i className={`text-2xl ${
                    decision === 'approved'
                      ? 'ri-checkbox-circle-line'
                      : 'ri-error-warning-line'
                  }`} />
                </div>

                <div>
                  <h2 className="font-bold text-gray-900">
                    {decision === 'approved'
                      ? `Approve ${typeLabels[decisionCase.type] || decisionCase.type}`
                      : `Reject ${typeLabels[decisionCase.type] || decisionCase.type}`}
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    {decisionCase.caseNumber}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-5">
              <p className="text-sm text-gray-600 mb-4">
                {decision === 'approved'
                  ? `Confirm that you have reviewed the ${typeLabels[decisionCase.type] || decisionCase.type} submission and want to approve this request.`
                  : `Provide the reason for rejecting this ${typeLabels[decisionCase.type] || decisionCase.type} request.`}
              </p>

              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {decision === 'approved'
                  ? 'Confirm Approval'
                  : 'Confirm Rejection'}
                {decision === 'rejected' && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>

              <textarea
                value={remark}
                onChange={e => setRemark(e.target.value)}
                rows={4}
                maxLength={500}
                placeholder={
                  decision === 'approved'
                    ? 'Enter optional approval remarks...'
                    : 'Explain why this submission is being rejected...'
                }
                className="w-full border border-gray-200 rounded-xl p-3 text-sm outline-none resize-none focus:border-teal-500"
              />

              <p className="text-xs text-gray-400 text-right mt-1">
                {remark.length}/500 characters
              </p>

              {decision === 'rejected' && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-3 mt-3">
                  <p className="text-xs text-red-700">
                    <i className="ri-information-line mr-1" />
                    The Social Worker will need to address this reason before resubmitting the case.
                  </p>
                </div>
              )}

              <div className="flex gap-3 mt-5">
                <button
                  disabled={submitting}
                  onClick={() => setDecisionCase(null)}
                  className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2.5 text-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  disabled={
                    submitting ||
                    (decision === 'rejected' && remark.trim() === '')
                  }
                  onClick={confirmDecision}
                  className={`flex-1 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-50 ${
                    decision === 'approved'
                      ? 'bg-teal-500 hover:bg-teal-600'
                      : 'bg-red-500 hover:bg-red-600'
                  }`}
                >
                  {submitting
                    ? 'Processing...'
                    : decision === 'approved'
                      ? 'Confirm Approval'
                      : 'Confirm Rejection'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
